// Admin setup utilities for Trivia Plug
import { auth, db } from './firebase';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { ADMIN_USER_IDS, ADMIN_EMAILS } from './adminUtils';
import { migrateExistingUsersToPlanSystem } from './fixUserDocument';

// Setup admin user for current authenticated user
export const setupCurrentUserAsAdmin = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found');
    }

    console.log('🔧 Setting up admin for user:', user.email);

    // Check if user is already in admin lists
    const isInAdminList = ADMIN_USER_IDS.includes(user.uid) || 
                         (user.email && ADMIN_EMAILS.includes(user.email));

    if (!isInAdminList) {
      console.log('⚠️ User not in admin lists, adding to ADMIN_USER_IDS...');
      console.log('Add this user ID to adminUtils.js:', user.uid);
    }

    // Create or update user document
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      // Create new admin user document
      await setDoc(userRef, {
        fullName: user.displayName || user.email.split('@')[0],
        email: user.email,
        role: 'admin',
        createdAt: new Date(),
        updatedAt: new Date(),
        // Legacy fields removed - using subscription-based system
      });
      console.log('✅ Created new admin user document');
    } else {
      // Update existing user to admin
      const userData = userDoc.data();
      if (userData.role !== 'admin') {
        await setDoc(userRef, {
          role: 'admin',
          updatedAt: new Date()
        }, { merge: true });
        console.log('✅ Updated user role to admin');
      } else {
        console.log('✅ User is already admin');
      }
    }

    return true;
  } catch (error) {
    console.error('❌ Error setting up admin:', error);
    return false;
  }
};

// Verify admin system is working
export const verifyAdminSystem = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        success: false,
        error: 'No authenticated user'
      };
    }

    console.log('🔍 Verifying admin system for user:', user.email);

    // Check admin lists
    const isInAdminList = ADMIN_USER_IDS.includes(user.uid) || 
                         (user.email && ADMIN_EMAILS.includes(user.email));

    // Check Firestore document
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.exists() ? userDoc.data() : null;
    const hasAdminRole = userData?.role === 'admin';

    // Test database access
    let canAccessDatabase = false;
    try {
      // const testQuery = await getDocs(collection(db, 'users')); // Removed unused variable
      await getDocs(collection(db, 'users'));
      canAccessDatabase = true;
    } catch (dbError) {
      console.error('Database access test failed:', dbError);
    }

    const result = {
      success: isInAdminList || hasAdminRole,
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      },
      checks: {
        inAdminList: isInAdminList,
        hasAdminRole: hasAdminRole,
        canAccessDatabase: canAccessDatabase
      },
      recommendations: []
    };

    // Generate recommendations
    if (!isInAdminList) {
      result.recommendations.push('Add user ID to ADMIN_USER_IDS in adminUtils.js');
    }
    if (!hasAdminRole) {
      result.recommendations.push('Update user role to admin in Firestore');
    }
    if (!canAccessDatabase) {
      result.recommendations.push('Check Firebase security rules');
    }

    return result;
  } catch (error) {
    console.error('❌ Error verifying admin system:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get all admin users
export const getAllAdminUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    const adminUsers = [];
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      if (userData.role === 'admin') {
        adminUsers.push({
          id: doc.id,
          ...userData
        });
      }
    });

    return adminUsers;
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return [];
  }
};

// Create admin user by email
export const createAdminByEmail = async (email, fullName = null) => {
  try {
    // Find user by email
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    let targetUser = null;
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      if (userData.email === email) {
        targetUser = { id: doc.id, ...userData };
      }
    });

    if (!targetUser) {
      throw new Error(`User with email ${email} not found`);
    }

    // Update user to admin
    const userRef = doc(db, 'users', targetUser.id);
    await setDoc(userRef, {
      role: 'admin',
      updatedAt: new Date()
    }, { merge: true });

    console.log(`✅ Made ${email} an admin`);
    return true;
  } catch (error) {
    console.error('Error creating admin by email:', error);
    return false;
  }
};

// Admin system status check
export const getAdminSystemStatus = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return {
        authenticated: false,
        adminAccess: false,
        message: 'No authenticated user'
      };
    }

    const verification = await verifyAdminSystem();
    const adminUsers = await getAllAdminUsers();

    return {
      authenticated: true,
      adminAccess: verification.success,
      currentUser: verification.user,
      checks: verification.checks,
      recommendations: verification.recommendations,
      totalAdminUsers: adminUsers.length,
      adminUsers: adminUsers.map(u => ({ id: u.id, email: u.email, fullName: u.fullName }))
    };
  } catch (error) {
    console.error('Error getting admin system status:', error);
    return {
      authenticated: false,
      adminAccess: false,
      error: error.message
    };
  }
}; 

  // Test admin access to all collections
  // const testAdminAccess = async () => {
  //   try {
  //     console.log('🔍 Testing admin access to all collections...');
  //     
  //     // Test access to users collection
  //     const usersQuery = query(collection(db, 'users'), limit(1));
  //     const usersSnapshot = await getDocs(usersQuery);
  //     console.log('✅ Users collection access:', usersSnapshot.docs.length > 0);
  //     
  //     // Test access to games collection
  //     const gamesQuery = query(collection(db, 'games'), limit(1));
  //     const gamesSnapshot = await getDocs(gamesQuery);
  //     console.log('✅ Games collection access:', gamesSnapshot.docs.length > 0);
  //     
  //     // Test access to teams collection
  //     const teamsQuery = query(collection(db, 'teams'), limit(1));
  //     const teamsSnapshot = await getDocs(teamsQuery);
  //     console.log('✅ Teams collection access:', teamsSnapshot.docs.length > 0);
  //     
  //     // Test access to venues collection
  //     const venuesQuery = query(collection(db, 'venues'), limit(1));
  //     const venuesSnapshot = await getDocs(venuesQuery);
  //     console.log('✅ Venues collection access:', venuesSnapshot.docs.length > 0);
  //     
  //     // Test access to supportTickets collection
  //     const ticketsQuery = query(collection(db, 'supportTickets'), limit(1));
  //     const ticketsSnapshot = await getDocs(ticketsQuery);
  //     console.log('✅ Support tickets collection access:', ticketsSnapshot.docs.length > 0);
  //     
  //     console.log('🎉 All admin access tests completed successfully!');
  //     return true;
  //   } catch (error) {
  //     console.error('❌ Admin access test failed:', error);
  //     return false;
  //   }
  // }; // Removed unused function 

// Run migration to add plan field to existing users
export const runPlanMigration = async () => {
  try {
    console.log('🚀 Starting plan migration...');
    const result = await migrateExistingUsersToPlanSystem();
    
    if (result.success) {
      console.log('✅ Plan migration completed successfully');
      console.log(`📊 Results: ${result.migratedCount} migrated, ${result.skippedCount} skipped, ${result.errorCount} errors`);
    } else {
      console.error('❌ Plan migration failed:', result.message);
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error running plan migration:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

// Check and clean up problematic users
export const checkProblematicUser = async (email) => {
  try {
    console.log('🔍 Checking problematic user:', email);
    
    // Import the check function
    const { checkUserExistsInAuth } = await import('./makeAdmin');
    
    // Check if user exists in Firebase Auth
    const authCheck = await checkUserExistsInAuth(email);
    console.log('📋 Auth check result:', authCheck);
    
    // Check if user exists in Firestore
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const usersQuery = query(collection(db, 'users'), where('email', '==', email));
    const usersSnapshot = await getDocs(usersQuery);
    
    let firestoreUser = null;
    usersSnapshot.forEach(doc => {
      firestoreUser = { id: doc.id, ...doc.data() };
    });
    
    console.log('📋 Firestore check result:', firestoreUser ? 'Found' : 'Not found');
    
    const result = {
      email,
      authExists: authCheck.exists,
      authAvailable: authCheck.available,
      firestoreExists: !!firestoreUser,
      firestoreUser: firestoreUser,
      recommendations: []
    };
    
    // Generate recommendations
    if (authCheck.exists && !firestoreUser) {
      result.recommendations.push('User exists in Firebase Auth but not in Firestore - needs manual deletion from Firebase Console');
    } else if (!authCheck.exists && firestoreUser) {
      result.recommendations.push('User exists in Firestore but not in Firebase Auth - can be deleted from Firestore');
    } else if (authCheck.exists && firestoreUser) {
      result.recommendations.push('User exists in both - can be deleted from both');
    } else {
      result.recommendations.push('User does not exist in either system - email is available');
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error checking problematic user:', error);
    return {
      success: false,
      message: error.message
    };
  }
}; 